import React, { FC, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { CardType } from 'features/payment/types';
import { CCFormField, IPaymentErrors, IPaymentState } from 'features/payment/types';

import { TextInput, TextInputMask } from 'components/ucl/text-input';
import { t } from 'state/intl/translations';
import { PaymentFieldVariations } from 'state/launchdarkly/variations';
import { primitive } from 'styles/constants';
import { isNative } from 'utils/environment';
import { creditCardNumberMask, expiryMask, getCursorPosition } from 'utils/masks';

import {
  CardNumberWrapper,
  StyledCvvWrapper,
  StyledExpiryCVVWrapper,
  StyledExpiryWrapper,
} from './styled';

interface IFirstDataCreditCardFormInputs {
  onChange: (name: CCFormField, value: string) => void;
  errors: IPaymentErrors;
  paymentValues: IPaymentState;
  supportedCardTypes?: CardType[];
  paymentFieldVariations: PaymentFieldVariations;
  validateCreditCardForm?: () => boolean;
  didBlur: Record<string, boolean>;
  validateOnBlur: (fieldName: string) => void;
  isCheckoutRedesign?: boolean;
}

const CreditCardIcon: FC<React.PropsWithChildren<{ cardType: string | null }>> = ({ cardType }) =>
  (cardType &&
    {
      AMEX: <Icon variant="amex" />,
      DISCOVER: <Icon variant="discover" />,
      DINERS_CLUB: <Icon variant="diners" />,
      JCB: <Icon variant="jcb" />,
      MASTERCARD: <Icon variant="mastercard" />,
      VISA: <Icon variant="visa" />,
    }[cardType]) || <Icon variant="whiteEmptyCreditCard" size="sm" />;

const FirstDataCreditCardInputsRedesign: FC<React.PropsWithChildren<
  IFirstDataCreditCardFormInputs
>> = ({ errors, onChange, paymentValues, paymentFieldVariations, validateOnBlur, didBlur }) => {
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isBackspaceKey, setIsBackspaceKey] = useState(false);
  const [isCardNumberInputFocused, setIsCardNumberInputFocused] = useState(false);
  const [isExpirationDateFocused, setIsExpirationDateFocused] = useState(false);
  const [isCVVFocused, setIsCVVFocused] = useState(false);

  const handleChangeText = (changedValue: string) => {
    if (!isNative) {
      // Adjusting Cursor position
      setCursorPosition(prevPosition => getCursorPosition(isBackspaceKey, prevPosition));
    }
    onChange('cardNumber', changedValue);
  };

  const handleKeyPress = (event: any) => {
    const key = event.nativeEvent.key;

    if (key === 'Backspace') {
      setIsBackspaceKey(true);
    } else {
      setIsBackspaceKey(false);
    }
  };

  const endIcon = paymentValues.isCardNumberValid ? (
    <CreditCardIcon cardType={paymentValues.cardType} />
  ) : (
    <Icon variant="whiteEmptyCreditCard" size="sm" />
  );

  return (
    <>
      {paymentFieldVariations.cardNumber && (
        <CardNumberWrapper marginBottom="0">
          <DatadogSessionReplayMask>
            <TextInputMask
              onChangeText={handleChangeText}
              label={t('creditCardNumber')}
              value={paymentValues.cardNumber}
              required
              testID="cardNumber"
              placeholder={!isCardNumberInputFocused ? '0000 0000 0000 0000' : ''}
              borderColor={primitive.$blackOpacity30}
              placeholderTextColor={primitive.$blackOpacity30}
              aria-label={
                !didBlur.cardNumber && !errors.didAttemptSubmit
                  ? t('creditCardNumber')
                  : t('pleaseProvideAValid16Digit')
              }
              onFocus={() => setIsCardNumberInputFocused(true)}
              onBlur={() => {
                validateOnBlur('cardNumber');
                setIsCardNumberInputFocused(false);
              }}
              errorMessage={
                !didBlur.cardNumber && !errors.didAttemptSubmit
                  ? ''
                  : errors.cardType || errors.cardNumber
              }
              autoComplete="cc-number"
              keyboardType="numeric"
              mask={creditCardNumberMask}
              autoFocus
              endIcon={endIcon}
              {...(!isNative && {
                onSelectionChange: event => {
                  const { selection } = event.nativeEvent;
                  setCursorPosition(selection.start);
                },
                onKeyPress: handleKeyPress,
                selection: { start: cursorPosition, end: cursorPosition },
              })}
            />
          </DatadogSessionReplayMask>
        </CardNumberWrapper>
      )}
      <StyledExpiryCVVWrapper marginBottom="0">
        {paymentFieldVariations.expiration && (
          <StyledExpiryWrapper marginBottom="0">
            <TextInputMask
              borderColor={primitive.$blackOpacity30}
              autoComplete="cc-exp"
              accessibilityLabel={
                !didBlur.expiry && !errors.didAttemptSubmit
                  ? ''
                  : t('pleaseProvideTheExpirationDate')
              }
              onFocus={() => setIsExpirationDateFocused(true)}
              onBlur={() => {
                validateOnBlur('expiry');
                setIsExpirationDateFocused(false);
              }}
              errorMessage={!didBlur.expiry && !errors.didAttemptSubmit ? '' : errors.expiry}
              placeholder={!isExpirationDateFocused ? 'MM / YY' : ''}
              placeholderTextColor={primitive.$blackOpacity30}
              keyboardType="numeric"
              label={t('expirationDate')}
              mask={expiryMask}
              onChangeText={(value: string) => {
                onChange('expiry', value);
              }}
              required
              testID="expiry"
              value={paymentValues.expiry || ''}
            />
          </StyledExpiryWrapper>
        )}
        {paymentFieldVariations.cvv && (
          <StyledCvvWrapper marginBottom="0">
            <DatadogSessionReplayMask>
              <TextInput
                borderColor={primitive.$blackOpacity30}
                accessibilityLabel={
                  !didBlur.cvv && !errors.didAttemptSubmit ? '' : t('pleaseProvideTheCreditCardCvv')
                }
                onChangeText={value => onChange('cvv', value)}
                label={t('cvv')}
                value={paymentValues.cvv || ''}
                required
                testID="cvv"
                onFocus={() => setIsCVVFocused(true)}
                placeholder={!isCVVFocused ? '123' : ''}
                onBlur={() => {
                  validateOnBlur('cvv');
                  setIsCVVFocused(false);
                }}
                placeholderTextColor={primitive.$blackOpacity30}
                errorMessage={!didBlur.cvv && !errors.didAttemptSubmit ? '' : errors.cvv}
                autoComplete="cc-csc"
                keyboardType="numeric"
                maxLength={4}
              />
            </DatadogSessionReplayMask>
          </StyledCvvWrapper>
        )}
      </StyledExpiryCVVWrapper>
    </>
  );
};

export default FirstDataCreditCardInputsRedesign;
